'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import { profileStateAtom } from '@/lib/atoms/profile'
import { cachedIdentityFromProfileState, identityFromProfileState } from '@/lib/identity'
import { Identity, ProfileState } from '@/lib/types/identity'

/**
 * The raw profile lifecycle state. Prefer `useIdentity` unless you need to
 * switch on the specific status.
 */
export function useProfileState(): ProfileState {
  return useAtomValue(profileStateAtom)
}

export interface UseIdentityResult {
  /** The full state, for consumers that need to switch on the status. */
  state: ProfileState

  /**
   * Identity facts from a confirmed session, or null. Non-null implies
   * /sso/profile validated the session, so this is what any gate or API
   * identity should use.
   */
  identity: Identity | null

  /**
   * Identity facts from whatever profile we hold, including a localStorage cache
   * while a refresh is in flight. For display and analytics; never for gating
   * access or supplying a uid.
   */
  cachedIdentity: Identity | null

  /**
   * Do we have profile data at all? Equivalent to `cachedIdentity !== null`.
   *
   * This is the "is this a signed-in user" question for analytics and UI
   * affordances. It is not an authorization signal; use
   * `identity`/`isAuthenticated` for anything that gates access or supplies a
   * uid.
   */
  isSignedIn: boolean

  /** Session confirmed and profile loaded. The safe gate for acting on identity. */
  isAuthenticated: boolean

  /**
   * Nothing is known yet: no held profile and no answer from the server.
   *
   * A revalidating visitor has a profile to render, so they are not resolving.
   * Named `isResolving` to avoid colliding with consumers' own `isLoading`.
   */
  isResolving: boolean

  /**
   * A held profile is being confirmed against the server. For consumers that must
   * not commit to something expensive to undo.
   */
  isRevalidating: boolean
}

/** Single entry point for "who is signed in" on the client. */
export function useIdentity(): UseIdentityResult {
  const state = useProfileState()

  return useMemo(() => {
    const identity = identityFromProfileState(state)
    const cachedIdentity = cachedIdentityFromProfileState(state)

    return {
      state,
      identity,
      cachedIdentity,
      isSignedIn: cachedIdentity !== null,
      isAuthenticated: identity !== null,
      // Only 'unknown'. A held profile is enough to render from, so revalidating
      // behind it must not read as loading (RWEB-9820).
      isResolving: state.status === 'unknown',
      isRevalidating: state.status === 'revalidating',
    }
  }, [state])
}
